import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import FeatureFlagService from '../service/featureFlag.service';
import AuthService from '../service/auth.service';
import { userHasAdminRole } from '../utils/userRoles';

const FeatureFlagContext = createContext();
const ADMIN_BYPASS_SUFFIX = '__admin_bypass_enabled';

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
};

export const FeatureFlagProvider = ({ children }) => {
  const [featureFlags, setFeatureFlags] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFeatureFlags();
    // Refresh every 5 minutes
    const interval = setInterval(loadFeatureFlags, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const loadFeatureFlags = async () => {
    try {
      const response = await FeatureFlagService.getAllFeatureFlags();
      if (response.data.success) {
        setFeatureFlags(response.data.data || {});
      }
    } catch (error) {
      console.error('Error loading feature flags:', error);
      // Default to all features enabled if API fails
      setFeatureFlags({});
    } finally {
      setLoading(false);
    }
  };

  const isFeatureEnabled = useCallback((featureKey) => {
    const adminBypassEnabled = featureFlags[`${featureKey}${ADMIN_BYPASS_SUFFIX}`] !== false;
    // Admins can preview disabled features only when this flag allows per-flag bypass.
    if (adminBypassEnabled && userHasAdminRole(AuthService.getCurrentUser())) {
      return true;
    }
    // If flag doesn't exist, default to enabled
    return featureFlags[featureKey] !== false;
  }, [featureFlags]);

  return (
    <FeatureFlagContext.Provider value={{ featureFlags, isFeatureEnabled, loading, refresh: loadFeatureFlags }}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

